import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ReactNode } from "react";

interface StepperStep {
  id: number;
  title: string;
  description: string;
  icon: ReactNode;
}

interface MobileStepContent {
  content: ReactNode;
  replaceDefaultCta?: boolean;
  /** Где показать content относительно кнопки шага; по умолчанию — под ней. */
  placement?: "beforeCta" | "afterCta";
}

interface StepperProps {
  currentStep: number; // 1, 2, или 3
  onStepClick?: (step: number) => void;
  steps: Array<StepperStep>;
  // Обработчики для интерактивных кнопок
  onFileSelect?: () => void;
  onTemplateSelect?: () => void;
  onProcessFile?: () => void;
  // Состояния для отображения выбранных элементов
  selectedFileName?: string;
  selectedTemplateName?: string;
  isProcessing?: boolean;
  renderMobileContent?: (stepId: number) => MobileStepContent | null;
}

const StepperComponent = ({ 
  currentStep, 
  steps, 
  onStepClick,
  onFileSelect,
  onTemplateSelect,
  onProcessFile,
  selectedFileName,
  selectedTemplateName,
  isProcessing = false,
  renderMobileContent,
}: StepperProps) => {
  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'pending';
  };

  const getStepClasses = (stepId: number) => {
    const status = getStepStatus(stepId);
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-emerald-500 shadow-lg shadow-emerald-200/50 hover:shadow-emerald-300/70';
      case 'active':
        return 'bg-gradient-to-br from-brand-500 to-brand-600 text-white border-brand-500 shadow-lg shadow-brand-200/50 hover:shadow-brand-300/70';
      case 'pending':
      default:
        return 'bg-gradient-to-br from-muted to-slate-100 text-muted-foreground border-border/50 hover:border-border hover:shadow-md';
    }
  };

  const getConnectorClasses = (fromStepId: number) => {
    return fromStepId < currentStep ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : 'bg-gradient-to-r from-gray-300 to-gray-200';
  };

  return (
    <div className="w-full">
      {/* Мобильная версия - вертикальная */}
      <div className="flex flex-col space-y-4 md:hidden">
        {steps.map((step, index) => {
          const mobileContent = renderMobileContent?.(step.id);
          const extraMobileContent = mobileContent?.content ?? null;
          const replaceDefaultCta = Boolean(mobileContent?.replaceDefaultCta);
          const contentBeforeCta = mobileContent?.placement === "beforeCta";

          return (
            <div key={step.id} className="flex items-start space-x-4">
            {/* Иконка и коннектор */}
            <div className="flex flex-col items-center">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 cursor-pointer hover:scale-105 ${getStepClasses(step.id)}`}
                onClick={() => onStepClick && onStepClick(step.id)}
              >
                {getStepStatus(step.id) === 'completed' ? (
                  <Check className="h-5 w-5" />
                ) : (
                  <span className="text-lg">{step.icon}</span>
                )}
              </div>
              
              {/* Вертикальная соединительная линия */}
              {index !== steps.length - 1 && (
                <div
                  className={`w-0.5 h-8 mt-2 transition-colors duration-300 ${getConnectorClasses(step.id)}`}
                />
              )}
            </div>

            {/* Контент шага */}
            <div className="flex-1 py-2 min-w-0">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                getStepStatus(step.id) === 'active' ? 'text-blue-600' : 'text-foreground'
              }`}>
                {step.title}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
              
              {extraMobileContent && contentBeforeCta && (
                <div className="mt-3 space-y-2">
                  {extraMobileContent}
                </div>
              )}

              {/* Интерактивные кнопки для мобильной версии */}
              <div className="mt-3">
                {step.id === 1 && !replaceDefaultCta && (
                  <div className="space-y-2">
                    {selectedFileName ? (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">✓ Файл выбран</p>
                        <p className="text-xs text-green-600 truncate">{selectedFileName}</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={onFileSelect}
                          className="mt-1 text-xs h-7"
                        >
                          Изменить файл
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        onClick={onFileSelect}
                        disabled={getStepStatus(step.id) === 'pending'}
                        className={`w-full text-sm ${
                          getStepStatus(step.id) === 'active' ? '' : 'opacity-50 cursor-not-allowed'
                        }`}
                        size="sm"
                      >
                        📁 Выбрать файл
                      </Button>
                    )}
                  </div>
                )}

                {step.id === 2 && !replaceDefaultCta && (
                  <div className="space-y-2">
                    {selectedTemplateName ? (
                      <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">✓ Шаблон выбран</p>
                        <p className="text-xs text-green-600 truncate">{selectedTemplateName}</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={onTemplateSelect}
                          className="mt-1 text-xs h-7"
                          disabled={getStepStatus(step.id) === 'pending'}
                        >
                          Изменить шаблон
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        onClick={onTemplateSelect}
                        disabled={getStepStatus(step.id) === 'pending'}
                        className={`w-full text-sm ${
                          getStepStatus(step.id) === 'pending' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        size="sm"
                      >
                        📋 Выбрать шаблон
                      </Button>
                    )}
                  </div>
                )}

                {step.id === 3 && !replaceDefaultCta && (
                  <div className="space-y-2">
                    {isProcessing ? (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-700 font-medium">⚙️ Обрабатывается...</p>
                        <p className="text-xs text-blue-600">Пожалуйста, подождите</p>
                      </div>
                    ) : (
                      <Button 
                        onClick={onProcessFile}
                        disabled={getStepStatus(step.id) === 'pending'}
                        className={`w-full text-sm ${
                          getStepStatus(step.id) === 'pending' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        size="sm"
                      >
                        ⚙️ Обработать файл
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {extraMobileContent && !contentBeforeCta && (
                <div className="mt-3 space-y-2">
                  {extraMobileContent}
                </div>
              )}
            </div>
          </div>
        );
        })}
      </div>

      {/* Десктопная версия - горизонтальная */}
      <div className="hidden md:flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step.id} className="flex-1 text-center">
            {/* Контейнер для иконки и коннектора */}
            <div className="flex items-center">
              {/* Левый коннектор */}
              {index !== 0 && (
                <div className="flex-1 h-0.5 transition-colors duration-300">
                  <div
                    className={`h-full ${getConnectorClasses(steps[index - 1].id)}`}
                  />
                </div>
              )}

              {/* Иконка шага */}
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 cursor-pointer mx-2 hover:scale-105 ${getStepClasses(step.id)}`}
                onClick={() => onStepClick && onStepClick(step.id)}
              >
                {getStepStatus(step.id) === 'completed' ? (
                  <Check className="h-6 w-6" />
                ) : (
                  <span className="text-xl">{step.icon}</span>
                )}
              </div>

              {/* Правый коннектор */}
              {index !== steps.length - 1 && (
                <div className="flex-1 h-0.5 transition-colors duration-300">
                  <div
                    className={`h-full ${getConnectorClasses(step.id)}`}
                  />
                </div>
              )}
            </div>

            {/* Текст шага */}
            <div className="mt-4">
              <h3 className={`text-sm font-medium transition-colors duration-300 ${
                getStepStatus(step.id) === 'active' ? 'text-blue-600' : 'text-foreground'
              }`}>
                {step.title}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">{step.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StepperComponent;
